// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A ÂNCORA VIAJA — «desde quando é que eu estou a olhar para isto?»
// ============================================================================
//
// 📄 §11 linha O, peça **P2**, de
//    `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`.
//    📗 **O cliente autorizou este ponto central a 2026-09-27** (*«sim autorizo»*), em separado
//    da migração 090 e depois de lhe ser apresentado que é **uma segunda decisão de
//    arquitectura, do peso da primeira** — e não um detalhe dela.
//
// ---------------------------------------------------------------------------
// O QUE É A ÂNCORA
// ---------------------------------------------------------------------------
//
//    A migração 090 deu a `alocacao` um carimbo de **quando** cada linha mudou e de **quem** a
//    mudou. Falta a outra metade da pergunta: **quando é que ESTE separador leu o plano?** Sem
//    ela o servidor sabe que a linha mudou às 14:32 e não sabe se isso foi antes ou depois de o
//    planeador a ter visto — logo não pode avisar sem avisar sempre.
//
//    A âncora é o instante em que o **retrato** que está no ecrã foi construído — não o instante
//    em que foi servido. É o `carregadoEm` da cache do mapa, e o servidor manda-o no cabeçalho
//    `x-mpt-ancora` de `GET /api/mapa/dados`.
//
// ⚠️⚠️ **É O INSTANTE DO RETRATO E NÃO O DA ENTREGA, e a diferença não é cosmética:** o mapa é
//    servido de uma cache construída uma vez e reutilizada. Uma linha alterada **depois** de o
//    retrato ser construído mas **antes** de ele ser entregue é invisível para quem o recebeu —
//    com a âncora na entrega, essa alteração não contava como conflito e o planeador decidia
//    sobre uma coisa que já não estava lá, que é exactamente o cenário que a linha O descreve.
//
// ⚠️⚠️ **E É O RELÓGIO DO SERVIDOR NAS DUAS PONTAS.** Um `new Date()` do navegador comparado com
//    um `now()` do PostgreSQL é uma comparação entre dois relógios: num portátil 3 minutos
//    adiantado o aviso **nunca** dispara, e num atrasado dispara **sempre**. Nenhuma das duas
//    avarias se vê. A âncora nasce no servidor, vai ao navegador e volta ao servidor sem nunca
//    ser lida por um relógio nosso — o navegador trata-a como uma cadeia de caracteres opaca.
//
// ---------------------------------------------------------------------------
// 🔴 PORQUE É QUE ISTO É UM PONTO CENTRAL E NÃO UMA PROPRIEDADE EM 25 PAINÉIS
// ---------------------------------------------------------------------------
//
//    Medido a 2026-09-27 em `apps/web/src`: há **52** `POST`, e a formulação partilhada
//    (`escreverNaRede`, em `rede.ts`) cobre **6 — 11,5 %**. Os outros **46**, em **25**
//    ficheiros, são `fetch` cru. A ETag **é** capturada num sítio só (`App.tsx`), mas isso é a
//    **captura** e não a **entrega**: para chegar às escritas teria de descer por propriedade a
//    ~25 painéis, e o 26.º nasceria sem ela — sem nada o denunciar, porque o sintoma de uma
//    âncora em falta é o sistema **não avisar**.
//
// ✅ **O que torna o ponto central possível — e é uma propriedade da linguagem, não um truque:**
//    um `fetch(...)` escrito à solta é uma leitura da propriedade `fetch` do objecto global **no
//    momento da chamada**, e não uma referência fixada na importação. Substituir
//    `globalThis.fetch` uma vez intercepta as **107** chamadas de `apps/web/src` sem mexer em
//    nenhuma delas. 📌 Medido: **zero** sítios guardam o `fetch` numa variável
//    (`const f = window.fetch`, `fetch.bind(...)`), logo não há nenhum que escape.
//
// ⚠️ **A única outra porta de rede é um `XMLHttpRequest`** em `medicao.tsx` — e é uma **isenção
//    de mérito**: só entra na compilação com `MEDIR=1`, e **lê** o mapa (um `GET`). A âncora só
//    viaja em escritas, logo aquele caminho nada tem para levar.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ ÂNCORA AUSENTE ≠ CONFLITO — e é aqui que se paga metade
// ---------------------------------------------------------------------------
//
//    Um separador que já estivesse aberto antes deste *deploy* nunca recebeu âncora nenhuma, e
//    **não pode** ser lido como «leu há muito tempo». Aqui isso sai por construção: sem âncora
//    **não se acrescenta cabeçalho nenhum**, e do lado do servidor uma comparação com o que não
//    existe nunca é conflito. 📌 **Prova-se nas DUAS metades** — com âncora o cabeçalho vai, sem
//    âncora **não** vai —, porque só a primeira passaria com o invólucro a mandar sempre.
//
// ---------------------------------------------------------------------------
// ⚠️ O INVÓLUCRO TEM DE SER TRANSPARENTE — 3 340 provas passam por ele
// ---------------------------------------------------------------------------
//
//    Três decisões, e cada uma fecha uma maneira de partir provas que não têm nada a ver com
//    esta peça:
//
//      · **A aridade preserva-se.** Um `fetch('/x')` continua a chegar ao alvo como
//        `fetch('/x')` e não como `fetch('/x', undefined)` — há provas que verificam os
//        argumentos exactos, e um segundo argumento a mais fá-las-ia falhar sobre um produto
//        são. Daí os parâmetros por `...args` e o reenvio por *spread*.
//      · **Só escritas, e só da NOSSA API.** Um `GET` não muda nada, logo não tem conflito que
//        anunciar; e um cabeçalho nosso num pedido a outra origem seria fuga de informação para
//        fora da GVA.
//      · **Instalar duas vezes não empilha dois invólucros.** A função fica **marcada**, e uma
//        segunda instalação sobre ela não faz nada. Sem isto, um módulo importado duas vezes
//        punha o cabeçalho duas vezes e a aridade deixava de ser a original.
// ============================================================================

import { cicloDoConflito } from './conflito.js';
import { CABECALHO_DA_JANELA, inicioDaFitaActual } from '../janela-do-ecra.js';

/** O cabeçalho por onde a âncora viaja. ⚠️ O servidor lê exactamente este nome. */
export const CABECALHO_ANCORA = 'x-mpt-ancora';

/**
 * Os métodos que **escrevem** — os únicos que levam âncora.
 *
 * ⚠️ Hoje o `web` só usa `POST` (medido: 52 chamadas, zero `PUT`/`PATCH`/`DELETE`). Os outros
 * três estão aqui na mesma, e de propósito: a pergunta «isto muda alguma coisa?» responde-se
 * pelo **método** e não pelo inventário de hoje, senão a primeira rota `DELETE` que alguém
 * escrever nasce sem âncora e nada o denuncia.
 */
export const METODOS_QUE_ESCREVEM: readonly string[] = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * A âncora do retrato que está no ecrã, ou `null` enquanto não houver nenhum.
 *
 * ⚠️ Módulo e não contexto de React de propósito: quem precisa dela não é um componente — é o
 * `fetch`, que é chamado de dentro de manipuladores de eventos, de efeitos e de funções soltas
 * espalhadas por 25 ficheiros. Um contexto obrigava cada um deles a ser um componente.
 */
let ancora: string | null = null;

/**
 * Regista a âncora do retrato que acabou de chegar.
 *
 * ⚠️ `null` limpa-a, e isso é uma resposta e não uma falha: um servidor que não manda o
 * cabeçalho (um ambiente ainda por publicar, um duplo de teste) deixa o sistema exactamente
 * como estava antes desta peça — sem âncora, sem aviso, sem recusa.
 */
export function registarAncoraDoRetrato(valor: string | null): void {
  ancora = valor === null || valor.trim() === '' ? null : valor;
}

/** A âncora em vigor. Exportada para as provas e para quem a queira mostrar. */
export function ancoraDoRetrato(): string | null {
  return ancora;
}

/** A marca que impede duas instalações de empilharem dois invólucros. */
const MARCA = '__ancoraInstalada';

type Marcavel = { [MARCA]?: boolean };

function urlDe(entrada: unknown): string {
  if (typeof entrada === 'string') return entrada;
  if (typeof URL !== 'undefined' && entrada instanceof URL) return entrada.href;
  if (typeof Request !== 'undefined' && entrada instanceof Request) return entrada.url;
  return '';
}

/**
 * `/api/…` **da mesma origem**, e nada mais.
 *
 * ⚠️ Sem `location` (um ambiente sem DOM) só o caminho relativo conta: com um endereço absoluto
 * e sem origem com que o comparar não há forma de saber se é nosso, e mandar o cabeçalho na
 * dúvida é mandá-lo para fora.
 */
function caminhoDaNossaApi(url: string): boolean {
  if (url.startsWith('/api/')) return true;
  if (typeof location === 'undefined') return false;
  try {
    const u = new URL(url, location.href);
    return u.origin === location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

/** É um pedido de ESCRITA à nossa própria API? Exportada para a prova a poder exercitar. */
export function levaAncora(entrada: unknown, init: RequestInit | undefined): boolean {
  const metodo = (
    init?.method ??
    (typeof Request !== 'undefined' && entrada instanceof Request ? entrada.method : 'GET')
  ).toUpperCase();
  if (!METODOS_QUE_ESCREVEM.includes(metodo)) return false;
  return caminhoDaNossaApi(urlDe(entrada));
}

/**
 * Instala o invólucro — **uma vez**, e é o ponto central da peça P2.
 *
 * ⚠️ Recebe o objecto onde instalar para as provas o poderem exercitar sem mexer no global do
 * processo de teste. A omissão é o global, que é o que corre no navegador.
 */
export function instalarAncoraNoFetch(alvo: { fetch: typeof fetch } = globalThis): void {
  const original = alvo.fetch;
  if ((original as unknown as Marcavel)[MARCA] === true) return;

  const envolvido = function envolvido(...args: Parameters<typeof fetch>): Promise<Response> {
    const [entrada, init] = args;
    const a = ancora;
    // 🔴 2026-10-23 · D-101 · o início da fita do ecrã vai na MESMA escrita — ver `janela-do-ecra.ts`.
    // ⚠️ 2026-10-26 · «Ir para…» · a fita vai em TODO o pedido à nossa API, leituras incluídas: o
    //    motor e as consultas recebem dias contados da fita do ecrã, e o servidor interpreta cada
    //    pedido na fita que ele traz. A âncora de edição continua só nas escritas.
    const fita = caminhoDaNossaApi(urlDe(entrada)) ? inicioDaFitaActual() : null;
    const escreve = levaAncora(entrada, init);
    const ancoraAqui = escreve ? a : null;
    // ⚠️ **A aridade preserva-se**: reenvia-se o `args` tal e qual. Ver o cabeçalho.
    if (ancoraAqui === null && fita === null) return original(...args);
    /**
     * Manda o **mesmo** pedido, com a âncora e com o que mais lhe derem.
     *
     * ⚠️ Os cabeçalhos do `init` mandam sobre os do `Request`, que é a regra do `fetch`.
     * Parte-se dos que já existirem, para não apagar um `content-type` posto por quem chamou.
     *
     * ⚠️⚠️ **O corpo vai byte a byte igual, e é isso que permite repetir sem compreender.** A
     * aceitação do conflito viaja em cabeçalho exactamente por isto — ver `conflito.ts`.
     */
    const enviar = (extra: Readonly<Record<string, string>> | null): Promise<Response> => {
      const cabecalhos = new Headers(
        init?.headers ??
          (typeof Request !== 'undefined' && entrada instanceof Request
            ? entrada.headers
            : undefined),
      );
      if (ancoraAqui !== null) cabecalhos.set(CABECALHO_ANCORA, ancoraAqui);
      if (fita !== null) cabecalhos.set(CABECALHO_DA_JANELA, fita);
      if (extra !== null) for (const [k, v] of Object.entries(extra)) cabecalhos.set(k, v);
      return original(entrada, { ...init, headers: cabecalhos });
    };
    // ---- ⚠️⚠️ O CICLO DO CONFLITO — §11 linha O, peça P3 (2026-09-28) --------------------
    //
    //   Sem âncora não se chega aqui, logo não há conflito que o servidor possa acusar: o ramo
    //   de cima devolveu `original(...args)` tal e qual. Com âncora, uma escrita pode voltar
    //   `409 conflito_de_edicao`, e é aqui — e **não** em cada um dos 11 painéis sem
    //   `SaidaDoTravao` — que ela ganha saída. Ver o cabeçalho de `conflito.ts`.
    //
    // ⚠️ Sem ninguém registado para perguntar, o `cicloDoConflito` devolve a resposta original
    //   **sem a ler nem a alterar**.
    // ⚠️ 2026-10-23 · sem âncora (só a fita) não há conflito de edição a aceitar: segue directo.
    return ancoraAqui === null ? enviar(null) : cicloDoConflito(enviar);
  };

  (envolvido as unknown as Marcavel)[MARCA] = true;
  alvo.fetch = envolvido as unknown as typeof fetch;
}
